import type { TestRunStep } from "../types";

/**
 * Metadata about a step's position in the tree
 */
export interface StepTreeMetadata {
  depth: number;
  isParent: boolean;
  parentPath: string | null;
  pathKey: string;
}

/**
 * Generate a unique path key from title_path
 */
export function getPathKey(titlePath: string[]): string {
  return titlePath.join(" > ");
}

/**
 * Get the parent path key from a title_path
 */
export function getParentPathKey(titlePath: string[]): string | null {
  if (titlePath.length <= 1) return null;
  return titlePath.slice(0, -1).join(" > ");
}

/**
 * Build tree metadata for each step
 * Returns a Map of step.id -> StepTreeMetadata
 */
export function buildStepTreeMetadata(steps: TestRunStep[]): Map<string, StepTreeMetadata> {
  const metadataMap = new Map<string, StepTreeMetadata>();
  const parentPaths = new Set<string>();

  // First pass: identify all paths and which are parents
  for (const step of steps) {
    const titlePath = step.title_path || [step.name];
    const parentPath = getParentPathKey(titlePath);

    if (parentPath) {
      parentPaths.add(parentPath);
    }
  }

  // Second pass: build metadata
  for (const step of steps) {
    const titlePath = step.title_path || [step.name];
    const pathKey = getPathKey(titlePath);
    const parentPath = getParentPathKey(titlePath);
    const depth = titlePath.length - 1;
    const isParent = parentPaths.has(pathKey);

    metadataMap.set(step.id, {
      depth,
      isParent,
      parentPath,
      pathKey,
    });
  }

  return metadataMap;
}

/**
 * Check if a step should be visible based on collapsed state
 */
export function isStepVisible(
  step: TestRunStep,
  metadata: StepTreeMetadata,
  collapsedPaths: Set<string>,
): boolean {
  // Root level steps (depth 0) are always visible
  if (metadata.depth === 0) return true;

  // Check if any ancestor is collapsed
  const titlePath = step.title_path || [step.name];
  for (let i = 1; i < titlePath.length; i++) {
    const ancestorPath = titlePath.slice(0, i).join(" > ");
    if (collapsedPaths.has(ancestorPath)) {
      return false;
    }
  }

  return true;
}

/**
 * Get steps that should be rendered, respecting collapse state
 */
export function getVisibleSteps(
  steps: TestRunStep[],
  metadataMap: Map<string, StepTreeMetadata>,
  collapsedPaths: Set<string>,
): TestRunStep[] {
  return steps.filter((step) => {
    const metadata = metadataMap.get(step.id);
    if (!metadata) return true;
    return isStepVisible(step, metadata, collapsedPaths);
  });
}

/**
 * Count children (direct and indirect) for a parent path
 */
export function countChildren(parentPathKey: string, steps: TestRunStep[]): number {
  return steps.filter((step) => {
    const titlePath = step.title_path || [step.name];
    const pathKey = getPathKey(titlePath);
    return pathKey !== parentPathKey && pathKey.startsWith(`${parentPathKey} > `);
  }).length;
}
